import type { ProductListItem } from '../products/types';
import { PRODUCT_CODE_TARGET, PRODUCT_NAME_TARGET } from './transform';

/**
 * Client mirror of `nest-backend/src/imports/pipeline/product-resolution.ts`.
 * The backend is the source of truth — it both validates and applies — so this
 * module reproduces the lookup and expansion semantics exactly, and the
 * advisory preview agrees with what the server will do.
 */

/** One product container selection, flattened to what the importer addresses. */
export interface ImportProductSelection {
  /** The container's stable key — the field a container edit is addressed by. */
  container_key: string;
  /** The selected option's code — the value a container edit carries. */
  option_code: string;
  container_option_id: string;
}

/** A partner Product, as the importer needs to see it. */
export interface ImportProduct {
  product_id: string;
  product_name: string;
  /** The "Product Code" — joined option codes. */
  product_key: string;
  /** Empty for a legacy product (hand-curated Run Sheet, no selections). */
  selections: ImportProductSelection[];
}

/** Narrow the products list payload down to what import resolution reads. */
export function toImportProducts(products: readonly ProductListItem[]): ImportProduct[] {
  return products.map((p) => ({
    product_id: p.product_id,
    product_name: p.product_name,
    product_key: p.product_key,
    selections: p.container_selections.map((s) => ({
      container_key: s.container_key,
      option_code: s.option_code,
      container_option_id: s.container_option_id,
    })),
  }));
}

/** Which product column the mapping addressed. */
export type ProductLookupField = 'code' | 'name';

/** The `product.*` target a mapping entry addresses, or null for any other target. */
export function productLookupFieldFor(targetField: string): ProductLookupField | null {
  if (targetField === PRODUCT_CODE_TARGET) return 'code';
  if (targetField === PRODUCT_NAME_TARGET) return 'name';
  return null;
}

/** The mapping-editor label for a product target — used in row error messages. */
export function productTargetLabel(field: ProductLookupField): string {
  return field === 'code' ? 'Product Code' : 'Product';
}

/**
 * Case-insensitive lookup tables over the partner's products. Values are arrays
 * so a value matching more than one product is reported as ambiguous rather
 * than silently binding to whichever row sorted first.
 */
export interface ProductIndex {
  byKey: Map<string, ImportProduct[]>;
  byName: Map<string, ImportProduct[]>;
}

export function buildProductIndex(products: readonly ImportProduct[]): ProductIndex {
  const byKey = new Map<string, ImportProduct[]>();
  const byName = new Map<string, ImportProduct[]>();
  const push = (map: Map<string, ImportProduct[]>, rawKey: string, product: ImportProduct) => {
    const key = rawKey.trim().toLowerCase();
    if (key === '') return;
    const list = map.get(key);
    if (list) list.push(product);
    else map.set(key, [product]);
  };
  for (const product of products) {
    push(byKey, product.product_key, product);
    push(byName, product.product_name, product);
  }
  return { byKey, byName };
}

export interface ProductResolution {
  /** The matched product; null when the cell was blank or did not resolve. */
  product: ImportProduct | null;
  /** Set when the cell was non-blank but could not be resolved unambiguously. */
  error?: string;
}

/**
 * Resolve one cell to a partner product.
 *
 * Matching mirrors `resolveContainerOption`: trimmed and case-insensitive. A
 * `product.code` cell matches on Product Code only — the code is the
 * deterministic key, and falling back to the name would make a typo'd code
 * silently resolve to an unrelated product. A `product.name` cell matches on
 * name first, then falls back to the code, because every product minted by the
 * planner's find-or-create carries `product_name === product_key`, so a user
 * mapping "Product" against a sheet of codes is doing the expected thing.
 *
 * A blank cell resolves to `{ product: null }` with no error — the caller
 * decides whether that is fatal (creating a row) or a no-op (updating one).
 */
export function resolveProduct(
  index: ProductIndex,
  field: ProductLookupField,
  raw: string,
): ProductResolution {
  const needle = raw.trim().toLowerCase();
  if (needle === '') return { product: null };

  const label = productTargetLabel(field);
  const matches =
    field === 'code'
      ? (index.byKey.get(needle) ?? [])
      : (index.byName.get(needle) ?? index.byKey.get(needle) ?? []);

  if (matches.length === 0) {
    return { product: null, error: `${label}: "${raw.trim()}" does not match any product.` };
  }
  // Distinct products only — a name that also equals its own code hits both
  // lookup tables, which is not an ambiguity.
  const distinct = [...new Map(matches.map((p) => [p.product_id, p])).values()];
  if (distinct.length > 1) {
    return {
      product: null,
      error: `${label}: "${raw.trim()}" matches ${distinct.length} products — cannot resolve.`,
    };
  }
  return { product: distinct[0]! };
}

/** The container key of the Product Type container — the one create is gated on. */
export const PRODUCT_TYPE_CONTAINER_KEY = 'product_type';

/** The product's Product Type option id, or null when it carries no such selection. */
export function productTypeOptionIdOf(product: ImportProduct): string | null {
  return (
    product.selections.find((s) => s.container_key === PRODUCT_TYPE_CONTAINER_KEY)
      ?.container_option_id ?? null
  );
}

/**
 * The product's selections as `{ field, value }` container writes — the exact
 * shape a container column mapping resolves to, so the apply path cannot tell
 * the two apart.
 *
 * Deliberately faithful rather than filtered: an option that has since been
 * archived is still emitted, because dropping it would make the planner's
 * find-or-create land on a *different* product than the one the sheet named.
 *
 * Product Type is excluded: on a create it is applied by the add step, and on
 * an update it is locked outside draft. Callers that need it use
 * {@link productTypeOptionIdOf}.
 */
export function productContainerWrites(product: ImportProduct): { field: string; value: string }[] {
  return product.selections
    .filter((s) => s.container_key !== PRODUCT_TYPE_CONTAINER_KEY)
    .map((s) => ({ field: s.container_key, value: s.option_code }));
}

/**
 * A product with no container selections cannot be expressed as container
 * edits at all, so the importer binds `work_orders.product_id` to it directly.
 */
export function isDirectBindProduct(product: ImportProduct): boolean {
  return product.selections.length === 0;
}
